import React, { useState, useMemo, useEffect } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Tooltip } from "@/components/common/Tooltip";
import { useDropdown } from "components/hooks/useDropdown";
import { DropdownMenu } from "components/common/DropdownMenu";
export interface SelectOption {
  label: string;
  value: string | number;
}

interface SelectDropdownProps<T = SelectOption> {
  options: T[];
  value: string | number | null;
  onChange: (value: string | number, option: T) => void;
  getOptionKey?: (option: T) => string | number;
  getOptionLabel?: (option: T) => string;
  renderOption?: (option: T, isSelected: boolean) => React.ReactNode;
  renderSelected?: (option: T) => React.ReactNode;
  searchable?: boolean;
  getSearchText?: (option: T) => string;
  errorMessage?: string;
  fallbackDisplay?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  menuWidthClass?: string;
  maxVisibleItems?: number;
  size?: "sm" | "md";
  centerOptions?: boolean;
  menuItemClassName?: string;
}

export function SelectDropdown<T = SelectOption>({
  options,
  value,
  onChange,
  getOptionKey,
  getOptionLabel,
  renderOption,
  renderSelected,
  searchable = false,
  getSearchText,
  errorMessage,
  fallbackDisplay,
  placeholder = "Select",
  disabled = false,
  className = "",
  menuWidthClass = "w-full",
  maxVisibleItems,
  size = "md",
  centerOptions = false,
  menuItemClassName,
}: SelectDropdownProps<T>): React.ReactElement {
  const [searchQuery, setSearchQuery] = useState("");
  const { isOpen, triggerRef, toggle, close } = useDropdown<HTMLButtonElement>({
    disabled,
  });

  const resolvedGetKey = getOptionKey ?? ((o: T) => (o as SelectOption).value);
  const resolvedGetLabel =
    getOptionLabel ?? ((o: T) => (o as SelectOption).label);
  const resolvedGetSearchText =
    getSearchText ?? ((o: T) => resolvedGetLabel(o).toLowerCase());

  useEffect(() => {
    if (isOpen) {
      triggerRef.current?.focus();
    } else {
      setSearchQuery("");
    }
  }, [isOpen, triggerRef]);

  const selected = useMemo(
    () => options.find((o) => resolvedGetKey(o) === value) ?? null,
    [options, value, resolvedGetKey],
  );

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchQuery.trim()) {
      return options;
    }
    const query = searchQuery.toLowerCase();
    return options.filter((o) => resolvedGetSearchText(o).includes(query));
  }, [options, searchable, searchQuery, resolvedGetSearchText]);

  const handleSelect = (option: T) => {
    onChange(resolvedGetKey(option), option);
    close();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!searchable) return;

    if (e.key === "Escape") {
      if (searchQuery) {
        setSearchQuery("");
        e.stopPropagation();
      } else {
        close();
      }
    } else if (e.key === "Enter" && isOpen && filteredOptions.length === 1) {
      handleSelect(filteredOptions[0]);
      e.preventDefault();
    } else if (e.key === "ArrowDown" && !isOpen) {
      toggle();
      e.preventDefault();
    } else if (
      isOpen &&
      e.key.length === 1 &&
      !e.ctrlKey &&
      !e.metaKey &&
      !e.altKey
    ) {
      setSearchQuery((prev) => prev + e.key);
      e.preventDefault();
    } else if (
      !isOpen &&
      e.key.length === 1 &&
      !e.ctrlKey &&
      !e.metaKey &&
      !e.altKey
    ) {
      toggle();
      setSearchQuery(e.key);
      e.preventDefault();
    } else if (e.key === "Backspace" && isOpen && searchQuery) {
      setSearchQuery((prev) => prev.slice(0, -1));
      e.preventDefault();
    }
  };

  const menuContent = (
    <>
      {filteredOptions.length === 0 ? (
        <div
          className={`menu-item text-fg-muted-regular ${centerOptions ? "text-center" : ""} ${menuItemClassName ?? ""}`}
        >
          No options found
        </div>
      ) : (
        filteredOptions.map((opt) => {
          const isSelected = resolvedGetKey(opt) === value;
          return (
            <button
              key={resolvedGetKey(opt)}
              onClick={() => handleSelect(opt)}
              className={`menu-item ${isSelected ? "text-fg-medium" : ""} ${centerOptions ? "text-center" : ""} ${menuItemClassName ?? ""}`}
            >
              {renderOption
                ? renderOption(opt, isSelected)
                : resolvedGetLabel(opt)}
            </button>
          );
        })
      )}
    </>
  );

  const triggerClass = size === "sm" ? "menu-trigger-sm" : "menu-trigger";

  const buttonClassName = `${triggerClass} ${
    disabled
      ? "opacity-60 cursor-not-allowed"
      : searchable && isOpen
        ? "cursor-text"
        : "cursor-pointer"
  } ${errorMessage ? "!border-error" : isOpen ? "border-accent" : ""} ${className}`;

  const button = (
    <button
      ref={triggerRef}
      type="button"
      onClick={toggle}
      onKeyDown={searchable ? handleKeyDown : undefined}
      disabled={disabled}
      className={buttonClassName}
      style={
        errorMessage
          ? { borderColor: "var(--color-error)", borderWidth: "1px" }
          : undefined
      }
    >
      {searchable && isOpen && searchQuery ? (
        <span className="text-accent-medium truncate flex-1">
          {searchQuery}
        </span>
      ) : selected ? (
        renderSelected ? (
          renderSelected(selected)
        ) : (
          <span className="truncate">{resolvedGetLabel(selected)}</span>
        )
      ) : value !== null && fallbackDisplay ? (
        fallbackDisplay
      ) : (
        <span className="text-fg-muted-regular truncate flex-1">
          {placeholder}
        </span>
      )}
      <KeyboardArrowDownIcon className="icon-xs shrink-0 ml-1" />
    </button>
  );

  return (
    <div className={`relative ${menuWidthClass}`}>
      {errorMessage ? (
        <Tooltip title={errorMessage}>
          <div className="flex-1 min-w-0">{button}</div>
        </Tooltip>
      ) : (
        button
      )}

      <DropdownMenu
        anchorRef={triggerRef}
        isOpen={isOpen}
        onClose={close}
        className={maxVisibleItems ? "scrollbar-overlay-hover" : ""}
        style={{
          maxHeight: maxVisibleItems ? `${maxVisibleItems * 34}px` : undefined,
          overflowY: maxVisibleItems ? "auto" : undefined,
        }}
      >
        {menuContent}
      </DropdownMenu>
    </div>
  );
}
